import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Avatar,
  Fade,
  Zoom,
} from '@mui/material';
import {
  Call as CallIcon,
  Close as CloseIcon,
  Videocam as VideocamIcon,
} from '@mui/icons-material';
import CallSocketService from '../../services/sub/CallSocketService';
import MessageService from '../../services/MessageService';
import { CONTEXTS } from 'figshelf-modules';
import { useCallOverlay } from '../../contexts/CallOverlayContext';


// Module-level flag to prevent duplicate call handling
let isHandlingCall = false;

const IncomingCallHandler = () => {
  const { useAuth } = CONTEXTS;
  const { isSignedIn } = useAuth();
  const { openCall } = useCallOverlay();
  
  const [incomingCall, setIncomingCall] = useState(null);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [ringtoneBlocked, setRingtoneBlocked] = useState(false);
  const pulseAnimation = useRef(null);

  const playRingtone = async () => {
    try {
      const ringtoneUrl = `${process.env.PUBLIC_URL || ''}/sounds/ringtone.mp3`;
      const audio = pulseAnimation.current || new Audio(ringtoneUrl);
      audio.loop = true;
      pulseAnimation.current = audio;
      await audio.play();
      setRingtoneBlocked(false);
    } catch (e) {
      if (e?.name === 'NotAllowedError') {
        setRingtoneBlocked(true);
      }
      // Allow retry on next user interaction
      if (pulseAnimation.current) {
        try {
          pulseAnimation.current.pause();
        } catch {
        }
        pulseAnimation.current = null;
      }
      console.log('Could not play ringtone:', e);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {

      if (!isSignedIn) {
        try {
          await CallSocketService.disconnect();
        } catch {
        }
        

        return;
      }

      try {
        await CallSocketService.connect();

        if (cancelled) return;
      } catch (err) {
        if (cancelled) return;
        console.error('📞 IncomingCallHandler: Failed to connect call socket:', err);
      }
    };

    run();

    // Listen for incoming calls
    CallSocketService.onIncomingCall(async (data) => {
      
      // Ignore if already handling a call
      if (isHandlingCall) {
        return;
      }

      // Show popup immediately (do not wait on network)
      setIncomingCall({
        callId: data.callId,
        callerId: data.callerId,
        callerName: data.callerName || 'Unknown',
        callerAvatar: data.callerAvatar || null,
        conversationId: data.conversationId,
        callType: data.callType || 'audio',
      });
      
      // Set immediate fallback info from payload (doesn't depend on API)
      setConversationInfo({
        name: data.callerName || 'Unknown',
        avatar: data.callerAvatar || null,
      });

      // Attempt to start ringtone (may be blocked until user interaction)
      playRingtone();

      // Fetch conversation details to get name and avatar (best-effort)
      try {
        const conversation = await MessageService.getConversation(data.conversationId);
        setConversationInfo({
          name: conversation.name || data.callerName || 'Unknown',
          avatar: conversation.avatar || data.callerAvatar || null,
        });
      } catch (error) {
        if (error?.code === 'ERR_CANCELED') {
          return;
        }
      }
    });

    // Listen for call ended/rejected events to dismiss the modal
    CallSocketService.onCallEnded((data) => {
      stopRingtone();
      setIncomingCall(null);
      setConversationInfo(null);
    });

    CallSocketService.onCallRejected((data) => {
      stopRingtone();
      setIncomingCall(null);
      setConversationInfo(null);
    });

    return () => {
      cancelled = true;
      stopRingtone();
      isHandlingCall = false;
    };
  }, [isSignedIn]);

  const stopRingtone = () => {
    if (pulseAnimation.current) {
      pulseAnimation.current.pause();
      try {
        pulseAnimation.current.currentTime = 0;
      } catch {
      }
      pulseAnimation.current = null;
    }
    setRingtoneBlocked(false);
  };

  useEffect(() => {
    if (!incomingCall || !ringtoneBlocked) return;

    const handler = () => {
      playRingtone();
    };

    window.addEventListener('pointerdown', handler, { once: true });
    window.addEventListener('keydown', handler, { once: true });
    return () => {
      window.removeEventListener('pointerdown', handler);
      window.removeEventListener('keydown', handler);
    };
  }, [incomingCall, ringtoneBlocked]);

  const handleAccept = () => {
    stopRingtone();
    
    if (incomingCall && !isHandlingCall) {
      
      // Mark as handling call to prevent duplicate modals
      isHandlingCall = true;

      openCall({
        callId: incomingCall.callId,
        recipientId: incomingCall.callerId,
        recipientName: conversationInfo?.name || incomingCall.callerName,
        recipientAvatar: conversationInfo?.avatar || incomingCall.callerAvatar,
        conversationId: incomingCall.conversationId,
        isIncoming: true,
        callType: incomingCall.callType,
      });
      
      setIncomingCall(null);
      setConversationInfo(null);
      
      // Reset after navigation
      setTimeout(() => {
        isHandlingCall = false;
      }, 3000);
    }
  };

  const handleReject = () => {
    stopRingtone();
    
    if (incomingCall && !isHandlingCall) {
      
      // Mark as handling to prevent duplicate modals
      isHandlingCall = true;
      
      // Send reject event through CallSocketService
      CallSocketService.rejectCall(incomingCall.callId, 'User declined');
      setIncomingCall(null);
      setConversationInfo(null);
      
      // Reset after rejection
      setTimeout(() => {
        isHandlingCall = false;
      }, 2000);
    }
  };

  if (!incomingCall) {
    return null;
  }

  return (
    <Dialog
      open={true}
      onClose={handleReject}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          overflow: 'visible',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <Box 
        sx={{ 
          p: 4, 
          pt: 5,
          textAlign: 'center',
          background: 'linear-gradient(180deg, rgba(25, 118, 210, 0.08) 0%, rgba(255,255,255,0) 40%)',
        }}
      >
        {/* Caller Avatar with subtle ring */}
        <Box sx={{ position: 'relative', display: 'inline-block', mb: 3, mt: 1 }}>
          <Avatar
            src={conversationInfo?.avatar || incomingCall.callerAvatar}
            alt={conversationInfo?.name || incomingCall.callerName}
            sx={{
              width: 100,
              height: 100,
              border: '3px solid',
              borderColor: 'primary.main',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            }}
            imgProps={{
              onError: () => {
                setConversationInfo((prev) => ({
                  ...prev,
                  avatar: null,
                }));
              },
            }}
          >
            <Typography variant="h3" color="primary.main">
              {(conversationInfo?.name || incomingCall.callerName || 'U').charAt(0).toUpperCase()}
            </Typography>
          </Avatar>
          
          {/* Subtle ringing indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              left: -4,
              right: -4,
              bottom: -4,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: 'success.main',
              animation: 'ring 2s ease-out infinite',
              '@keyframes ring': {
                '0%': { transform: 'scale(1)', opacity: 0.8 },
                '100%': { transform: 'scale(1.15)', opacity: 0 },
              },
            }}
          />
        </Box>

        {/* Caller Info */}
        <Typography variant="h5" fontWeight="600" gutterBottom color="text.primary">
          {conversationInfo?.name || incomingCall.callerName}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 0.5 }}>
          {incomingCall.callType === 'video' ? 'Video Call' : 'Voice Call'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ opacity: 0.7, mb: 4 }}>
          Incoming call...
        </Typography>

        {ringtoneBlocked && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Tap anywhere to enable ringtone
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
          {/* Decline Button */}
          <Box sx={{ textAlign: 'center' }}>
            <IconButton
              onClick={handleReject}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: 'error.main',
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'error.dark',
                },
                transition: 'transform 0.15s, background-color 0.15s',
                boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
              }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <Typography variant="caption" display="block" mt={1} color="text.secondary">
              Decline
            </Typography>
          </Box>

          {/* Accept Button */}
          <Box sx={{ textAlign: 'center' }}>
            <IconButton
              onClick={handleAccept}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: 'success.main',
                color: '#fff',
                '&:hover': {
                  backgroundColor: 'success.dark',
                },
                transition: 'transform 0.15s, background-color 0.15s',
                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.3)',
              }}
            >
              {incomingCall.callType === 'video' ? (
                <VideocamIcon sx={{ fontSize: 28 }} />
              ) : (
                <CallIcon sx={{ fontSize: 28 }} />
              )}
            </IconButton>
            <Typography variant="caption" display="block" mt={1} color="text.secondary">
              Accept
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default IncomingCallHandler;
